import { ref, unref } from 'vue'
import type { Ref } from 'vue'

// Define the shape of our split email for strict returns
interface EmailParts {
  local: string
  domain: string
}

export function useEmailCheck(popularDomainsRef: Ref<string[]> | string[]) {
  // Holds the single best typo correction (e.g., for onBlur events)
  const closestMatch = ref<string>('')

  /**
   * Standard Levenshtein distance algorithm.
   */
  const getLevenshteinDistance = (a: string, b: string): number => {
    const matrix: number[][] = []

    for (let i = 0; i <= b.length; i++) matrix[i] = [i]
    for (let j = 0; j <= a.length; j++) matrix[0]![j] = j

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i]![j] = matrix[i - 1]![j - 1]!
        } else {
          matrix[i]![j] = Math.min(
            matrix[i - 1]![j - 1]! + 1, // substitution
            matrix[i]![j - 1]! + 1, // insertion
            matrix[i - 1]![j]! + 1, // deletion
          )
        }
      }
    }

    return matrix[b.length]![a.length]!
  }

  /**
   * Splits an email safely, accounting for edge cases
   * where a local part might contain an '@' character.
   */
  const splitEmail = (email: string): EmailParts | null => {
    const atIndex = email.lastIndexOf('@')
    if (atIndex === -1) return null

    return {
      local: email.slice(0, atIndex),
      domain: email.slice(atIndex + 1).toLowerCase(),
    }
  }

  /**
   * Collapses adjacent identical labels in a domain
   * (e.g. 'gmail.com.com' → 'gmail.com'). Pure helper.
   */
  const dedupAdjacentLabels = (domain: string): string => {
    const out: string[] = []
    for (const label of domain.split('.')) {
      if (out[out.length - 1] !== label) out.push(label)
    }

    return out.join('.')
  }

  /**
   * Finds a domain we should suggest in place of `domain` when it looks like a typo.
   * Used by both `checkTypo` (blur message) and `getAutocompleteSuggestions` (dropdown).
   */
  const findCloseTypoSuggestion = (domain: string): string | null => {
    const domains = unref(popularDomainsRef)

    for (const d of domains) {
      let n = 0
      while (n < domain.length && n < d.length && domain.charAt(n) === d.charAt(n)) n++
      if (n >= d.length - 1 && domain.length > n && domain !== d) return d
    }

    const deduped = dedupAdjacentLabels(domain)
    if (deduped !== domain && deduped.includes('.')) return deduped

    return null
  }

  /**
   * Checks for a typo (<= 2 chars off).
   * Updates the `closestMatch` ref and returns the formatted suggestion.
   */
  const checkTypo = (email: string): string | null => {
    closestMatch.value = ''
    const parts = splitEmail(email)
    if (!parts || !parts.domain) return null

    const domains = unref(popularDomainsRef)

    // Exact match found; no typo
    if (domains.includes(parts.domain)) return null

    // Duplicated-label / near-popular typo (e.g. 'gmail.co.co' → 'gmail.com',
    // 'mybrand.lv.lv' → 'mybrand.lv'). Take precedence over Levenshtein — these are more
    // confident fixes than a generic edit-distance guess.
    const closeSuggestion = findCloseTypoSuggestion(parts.domain)
    if (closeSuggestion) {
      closestMatch.value = `${parts.local}@${closeSuggestion}`

      return closestMatch.value
    }

    let bestDomain: string | null = null
    let minDistance: number = Infinity

    for (const d of domains) {
      const distance = getLevenshteinDistance(parts.domain, d)
      if (distance <= 2 && distance < minDistance) {
        minDistance = distance
        bestDomain = d
      }
    }

    if (bestDomain) {
      closestMatch.value = `${parts.local}@${bestDomain}`
      return closestMatch.value
    }

    return null
  }

  /**
   * Generates an ordered list of autocomplete suggestions.
   * Prioritizes prefix matches first, then sorts the rest by typo distance.
   * Judges typos by cropping the target domain to the user's current input length.
   */
  const getAutocompleteSuggestions = (email: string, limit: number = 5): string[] => {
    const parts = splitEmail(email)
    if (!parts) return [] // Only suggest once they type '@'
    if (parts.local.includes('@')) return [] // Don't suggest doubled domains for multi-'@' input

    const domains = unref(popularDomainsRef)
    const inputDomain = parts.domain

    // --- NEW: If they just typed '@', return the original list in order ---
    if (inputDomain === '') {
      return domains.slice(0, limit).map((d) => `${parts.local}@${d}`)
    }

    // Surface duplicated-label / near-popular typos at the top of the list.
    const closeSuggestion = findCloseTypoSuggestion(inputDomain)
    const dedupSuggestion = closeSuggestion ? `${parts.local}@${closeSuggestion}` : null

    const inputLen = inputDomain.length

    // 1. Filter out completely unrelated domains
    const filteredDomains = domains.filter((d: string) => {
      const startsWith = d.startsWith(inputDomain)

      // Crop the domain to the length of the input for a fair 'in-progress' typo check
      const croppedD = d.slice(0, inputLen)
      const isCloseTypo = getLevenshteinDistance(inputDomain, croppedD) <= 2

      return startsWith || isCloseTypo
    })

    // 2. Sort the remaining valid domains.
    // `domains` is ordered by popularity, so use its index as the popularity rank.
    const rankOf = (d: string): number => domains.indexOf(d)
    const sortedDomains = filteredDomains.sort((a: string, b: string) => {
      const aStarts = a.startsWith(inputDomain)
      const bStarts = b.startsWith(inputDomain)

      // Prioritize domains that start with the input
      if (aStarts && !bStarts) return -1
      if (!aStarts && bStarts) return 1

      // If both start with the input, order by popularity (array order), not
      // alphabetically/by length — the most popular matching domain comes first.
      if (aStarts && bStarts) return rankOf(a) - rankOf(b)

      // Neither starts with the input: sort by Levenshtein distance (typo
      // closeness) using the cropped versions, breaking ties by popularity.
      const croppedA = a.slice(0, inputLen)
      const croppedB = b.slice(0, inputLen)
      const distDiff =
        getLevenshteinDistance(inputDomain, croppedA) -
        getLevenshteinDistance(inputDomain, croppedB)

      return distDiff !== 0 ? distDiff : rankOf(a) - rankOf(b)
    })

    // Build the final list: dedup suggestion first (if any), then popular matches,
    // de-duplicated and capped at `limit`.
    const candidates = sortedDomains.map((d) => `${parts.local}@${d}`)
    if (dedupSuggestion) candidates.unshift(dedupSuggestion)
    const result: string[] = []
    for (const suggestion of candidates) {
      if (!result.includes(suggestion)) result.push(suggestion)
      if (result.length >= limit) break
    }

    return result
  }

  /**
   * Extremely loose email validation.
   * Checks that the string matches an "a@a.a" pattern (at least 1 char per part).
   * Assumes the live input filter has already eliminated '..', '@@', and '.' immediately after '@'.
   */
  const isBasicValid = (email: string): boolean => {
    if (!email || email.includes(' ')) return false

    // RFC length caps, mirroring the BE's `email-validator` (orders/api
    // rejects with 422 on create when these are exceeded — an FE pass here
    // followed by a BE reject would leave the visitor orderless for the rest
    // of the funnel): total ≤ 254, local part ≤ 64.
    if (email.length > 254) return false

    // Allowed charset: ASCII letters/digits + a small set of punctuation.
    // '+' is intentionally permitted for QA test emails
    // Rejects accented and other non-ASCII characters that downstream systems might not handle well
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+$/.test(email)) return false

    const atIndex = email.indexOf('@')
    if (atIndex > 64) return false
    const dotIndex = email.lastIndexOf('.')

    if (
      atIndex <= 0 || // 1. At least one char BEFORE the '@' (e.g., 'a')
      dotIndex <= atIndex + 1 || // 2. At least one char BETWEEN the '@' and the '.' (e.g., 'a')
      dotIndex >= email.length - 1 // 3. At least one char AFTER the '.' (e.g., 'a')
    ) {
      return false
    }

    // Reject leading/trailing dot or hyphen in the local part
    // (e.g., '.foo@bar.com', 'foo.@bar.com', '-foo@bar.com', 'foo-@bar.com')
    const localPart = email.slice(0, atIndex)
    if (/^[.-]|[.-]$/.test(localPart)) return false

    // Walk domain labels. Rejects labels with leading/trailing hyphens
    // (e.g., 'foo@-bar.com', 'foo@bar-.com') and identical adjacent labels
    const domainLabels = email
      .slice(atIndex + 1)
      .toLowerCase()
      .split('.')
    for (let i = 0; i < domainLabels.length; i++) {
      const label = domainLabels[i]!
      if (label.startsWith('-') || label.endsWith('-')) return false
      if (i > 0 && label === domainLabels[i - 1]) return false
    }

    // Reject hyphens or digits anywhere in the TLD
    if (/[^a-z]/.test(domainLabels[domainLabels.length - 1]!)) return false

    return true
  }

  /**
   * Collapses '..' → '.', '@@' → '@', drops any '.' immediately after '@', strips
   * diacritics so accented input becomes ASCII (e.g. 'josé' → 'jose'), lowercases
   * every character (so a soft keyboard's auto-capitalized first letter doesn't
   * leak into the value), and drops characters outside the email allow-set
   * ([a-z0-9._%+-@]) — same charset `isBasicValid` accepts, with '+' intentionally
   * permitted for QA test emails. Returns the cleaned string plus the new cursor
   * position (so callers can restore selection without it jumping to the end).
   */
  const EMAIL_ALLOWED_CHAR = /^[a-zA-Z0-9._%+\-@]$/
  const cleanEmailValue = (email: string, cursor: number): { cleaned: string; cursor: number } => {
    let cleaned = ''
    let newCursor = cursor
    for (let i = 0; i < email.length; i++) {
      // Per-char NFD + diacritic strip handles both precomposed input ('é' → 'e') and
      // pre-decomposed input (lone combining mark → ''), keeping cursor math exact in both.
      const char = email
        .charAt(i)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
      const lastClean = cleaned.charAt(cleaned.length - 1)
      const drop =
        !char ||
        !EMAIL_ALLOWED_CHAR.test(char) ||
        (char === '.' && (lastClean === '.' || lastClean === '@')) ||
        (char === '@' && lastClean === '@')
      if (drop) {
        if (i < cursor) newCursor--
      } else {
        cleaned += char
      }
    }

    return { cleaned, cursor: newCursor }
  }

  return {
    closestMatch,
    checkTypo,
    getAutocompleteSuggestions,
    isBasicValid,
    cleanEmailValue,
  }
}
